import * as React from 'react'

import type {
  FilterSelection,
  GroupKey,
  ISortState,
} from './applyMapViewsTransform'
import { MapViewsSidebarContent } from './MapViewsSidebarContent'
import { IMapViewItem, IMapViewRow } from './MapViewsSidebarListItem'
import { PublishToAppKey } from './PublishToChipRow'
import { ModalSurface, PopoverSurface, SheetSurface } from './surfaces'

/** Every field optional at mount (`initialView`) and at notify (`onViewChange`). */
export interface IMapViewsSidebarView {
  searchTerm?: string
  sort?: ISortState
  group?: GroupKey
  filterSelection?: FilterSelection
}

// Three surface modes (Figma 12507:87721 modal). Open state is consumer-controlled.

type PopoverSide = 'top' | 'bottom' | 'left' | 'right'
type PopoverAlign = 'start' | 'center' | 'end'

interface IBaseProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title?: string
  items: IMapViewItem[]
  selectedId?: string
  onSelect: (id: string) => void
  /** Promotion gated by `useActiveLiveMapSlot` (singleton). Omit to disable previews entirely. */
  renderLivePreview?: (item: IMapViewRow) => React.ReactNode
  /** Each control gates on its own callback (menu needs rename+delete; handle needs reorder). */
  canEdit?: boolean
  onRename?: (id: string) => void
  onDelete?: (id: string) => void
  onCopy?: (id: string) => void
  onSetLocation?: (id: string, anchor: HTMLElement) => void
  onReorder?: (
    fromId: string,
    toId: string,
    position: 'before' | 'after',
  ) => void
  /** Apps absent from this list are hidden from the chip row and Publish-to popup. */
  availableApps?: PublishToAppKey[]
  onOpenPublishTo?: (id: string, anchor: HTMLElement) => void
  /** In-tree mount point so nested dialogs land inside Radix's focus trap (otherwise focus is stolen on each keystroke). */
  nestedDialogPortalRef?: React.RefObject<HTMLDivElement | null>
  /** Sticky footer with top border per Figma 12507:87722. */
  footerSlot?: React.ReactNode
  initialView?: IMapViewsSidebarView
  onViewChange?: (view: IMapViewsSidebarView) => void
}

interface IModalProps extends IBaseProps {
  surface: 'modal'
  /** Override modal width (px). Defaults to 512 (Figma 12507:87721). */
  modalWidthPx?: number
}

interface IDrawerProps extends IBaseProps {
  surface: 'drawer'
}

interface IPopoverProps extends IBaseProps {
  surface: 'popover'
  /** Rendered inside the popover root; must contain a `PopoverTrigger` over a real button. */
  trigger: React.ReactNode
  /** The popup sits against this element, and a press inside it never dismisses the popup. */
  anchor: React.RefObject<HTMLElement>
  side?: PopoverSide
  align?: PopoverAlign
  sideOffset?: number
  /** Portal target (e.g. the fullscreen element) so the popover renders in the
   *  top layer during browser fullscreen; defaults to document.body. */
  portalContainer?: HTMLElement
}

type IMapViewsSidebarProps = IModalProps | IDrawerProps | IPopoverProps

export const MapViewsSidebar: React.FC<IMapViewsSidebarProps> = props => {
  const {
    open,
    onOpenChange,
    title,
    items,
    selectedId,
    onSelect,
    renderLivePreview,
    canEdit,
    onRename,
    onDelete,
    onCopy,
    onSetLocation,
    onReorder,
    nestedDialogPortalRef,
    footerSlot,
    availableApps,
    onOpenPublishTo,
    initialView,
    onViewChange,
  } = props
  const handleClose = React.useCallback(
    () => onOpenChange(false),
    [onOpenChange],
  )

  // popover/drawer size to content via `max-h-*` with no `h-*` pin — CardList must drop
  // `size` containment to bubble its measured height up. modal stays fixed at h-[85vh].
  const parentSizesToContent =
    props.surface === 'popover' || props.surface === 'drawer'

  const content = (
    <MapViewsSidebarContent
      title={title}
      items={items}
      selectedId={selectedId}
      onSelect={onSelect}
      onClose={handleClose}
      renderLivePreview={renderLivePreview}
      canEdit={canEdit}
      onRename={onRename}
      onDelete={onDelete}
      onCopy={onCopy}
      onSetLocation={onSetLocation}
      onReorder={onReorder}
      footerSlot={footerSlot}
      availableApps={availableApps}
      onOpenPublishTo={onOpenPublishTo}
      initialView={initialView}
      onViewChange={onViewChange}
      parentSizesToContent={parentSizesToContent}
    />
  )

  if (props.surface === 'popover') {
    return (
      <PopoverSurface
        open={open}
        onOpenChange={onOpenChange}
        trigger={props.trigger}
        anchor={props.anchor}
        side={props.side}
        align={props.align}
        sideOffset={props.sideOffset}
        container={props.portalContainer}
      >
        {content}
      </PopoverSurface>
    )
  }

  if (props.surface === 'modal') {
    return (
      <ModalSurface
        open={open}
        onOpenChange={onOpenChange}
        nestedDialogPortalRef={nestedDialogPortalRef}
        widthPx={props.modalWidthPx}
      >
        {content}
      </ModalSurface>
    )
  }

  return (
    <SheetSurface
      open={open}
      onOpenChange={onOpenChange}
      nestedDialogPortalRef={nestedDialogPortalRef}
    >
      {content}
    </SheetSurface>
  )
}
